/**
 * Live screen: H.264 over a plain HTTP/1.1 download, decoded with WebCodecs.
 *
 * Ported from the working viewer. The behaviour below is load-bearing:
 *
 * - **One object per session.** The old code kept the video loop in shared state, so a stream from
 *   the previous phone could wipe the *new* session's id while unwinding — video kept playing while
 *   input died with "no active session". Here a stopped stream can only touch its own fields.
 * - **Decode every frame, paint only the newest.** H.264 is differential: dropping a P-frame tears
 *   the picture until the next keyframe, and scrcpy is stingy with keyframes. But painting every
 *   decoded frame in order makes motion feel like jelly — smooth and always slightly behind. So we
 *   feed the decoder everything and, per animation frame, draw only the freshest frame.
 * - **Reconnect on any silence.** Reopening `/video` restarts the whole chain (the server
 *   re-dispatches `stream_start`, the hub restarts scrcpy), which heals net blips, hub reconnects
 *   and a wedged encoder alike.
 * - **Ask for quality only right after the tester changed it.** On a plain (re)connect we request
 *   nothing so the hub reuses the running stream; otherwise two viewers with different presets
 *   restart the stream in turn and neither gets a stable picture.
 */

import type { TikTokHubApi } from '../api/client'
import { lostThePhone, type LostReason } from '../features/session/phoneHold'
import { AnnexBDemuxer, codecFromSps, joinAccessUnit } from './h264'
import { StreamWatchdog } from './watchdog'

export interface VideoStats {
  fps: number
  kbit: number
  queue: number
  decodeMs: number
}

export interface VideoStreamOptions {
  api: TikTokHubApi
  sessionId: string
  canvas: HTMLCanvasElement
  /** "720x1600:3000000" — sent on the next request only, then forgotten. */
  quality?: string | null
  onStatus?: (text: string) => void
  onStats?: (stats: VideoStats) => void
  onFirstFrame?: () => void
  /** The server says the session is gone (404) — do not reconnect. */
  /** The session no longer owns the phone: a 404 (ended) or a 409 the service explains (`LostReason`). */
  onSessionEnded?: (reason: LostReason | null) => void
}

const FRAME_TS_STEP = 33333 // µs; the decoder only needs timestamps that advance

export class VideoStream {
  private disposed = false
  private abort: AbortController | null = null
  private decoder: VideoDecoder | null = null
  private statTimer: ReturnType<typeof setInterval> | null = null
  private stallTimer: ReturnType<typeof setInterval> | null = null
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private quality: string | null
  private readonly watchdog = new StreamWatchdog()

  private bytesIn = 0
  private rendered = 0
  private statAt = 0
  private decodeMs = 0
  private fedAt: number[] = []
  private pending: VideoFrame | null = null
  private painting = false
  private ready = false

  constructor(private readonly o: VideoStreamOptions) {
    this.quality = o.quality ?? null
  }

  start(): void {
    if (this.disposed) return
    if (typeof VideoDecoder === 'undefined') {
      this.o.onStatus?.('потрібен Chrome/Edge (WebCodecs)')
      return
    }
    this.o.onStatus?.('хаб піднімає потік…') // ~5s: dispatch → scrcpy boot → control + SPS ready
    this.watchdog.begin(Date.now())
    this.statAt = Date.now()
    this.startWatchdogs()
    void this.run()
  }

  /** Manual quality switch: restarts the stream with new encoder settings. */
  setQuality(quality: string): void {
    this.quality = quality
    this.o.onStatus?.('зміна якості…')
    this.teardownStream()
    // let the hub restart scrcpy before we ask for the new stream; a restart is a cold start again,
    // so the watchdog goes back to the startup budget
    this.watchdog.begin(Date.now())
    this.retryTimer = setTimeout(() => this.run(), 300)
  }

  stop(): void {
    this.disposed = true
    this.teardownStream()
    this.clearTimer('statTimer')
    this.clearTimer('stallTimer')
    if (this.pending) {
      this.pending.close()
      this.pending = null
    }
  }

  // ---- internals ----

  private teardownStream(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    if (this.abort) {
      this.abort.abort()
      this.abort = null
    }
    if (this.decoder) {
      try {
        this.decoder.close()
      } catch {
        /* already closed */
      }
      this.decoder = null
    }
  }

  private clearTimer(which: 'statTimer' | 'stallTimer'): void {
    const t = this[which]
    if (t) {
      clearInterval(t)
      this[which] = null
    }
  }

  private startWatchdogs(): void {
    this.clearTimer('statTimer')
    this.statTimer = setInterval(() => {
      const dt = (Date.now() - this.statAt) / 1000 || 1
      const stats: VideoStats = {
        fps: Math.round(this.rendered / dt),
        kbit: Math.round((this.bytesIn * 8) / dt / 1000),
        queue: this.decoder?.decodeQueueSize ?? 0,
        decodeMs: this.decodeMs,
      }
      this.bytesIn = 0
      this.rendered = 0
      this.statAt = Date.now()
      this.o.onStats?.(stats)
    }, 1000)

    this.clearTimer('stallTimer')
    this.stallTimer = setInterval(() => {
      if (this.watchdog.expired(Date.now())) {
        this.retry(this.watchdog.started ? 'stall' : 'хаб не віддав потік')
      }
    }, 1000)
  }

  private retry(reason: string): void {
    if (this.disposed) return
    this.teardownStream()
    this.o.onStatus?.(`перепідключення… (${reason})`)
    // A reconnect re-dispatches stream_start, so the hub boots scrcpy from cold again: the next
    // attempt gets the startup budget, not the steady-state one. Without this the retries were
    // self-defeating — each one was cut off before the stream it had just asked for could begin.
    this.watchdog.begin(Date.now())
    this.retryTimer = setTimeout(() => {
      if (!this.disposed) void this.run()
    }, 1000)
  }

  private makeDecoder(): VideoDecoder {
    const decoder = new VideoDecoder({
      output: (frame) => {
        if (this.disposed) {
          frame.close()
          return
        }
        this.watchdog.bytes(Date.now())
        if (this.pending) this.pending.close() // superseded before it was ever shown
        this.pending = frame
        if (!this.painting) {
          this.painting = true
          requestAnimationFrame(() => this.paint())
        }
        const t0 = this.fedAt.shift()
        if (t0) this.decodeMs = Date.now() - t0
      },
      error: (e) => {
        console.warn('decoder error', e)
        this.retry('decode')
      },
    })
    this.decoder = decoder
    return decoder
  }

  private paint(): void {
    this.painting = false
    const frame = this.pending
    this.pending = null
    if (!frame) return
    if (this.disposed) {
      frame.close()
      return
    }
    const canvas = this.o.canvas
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      frame.close()
      return
    }
    if (canvas.width !== frame.displayWidth) {
      canvas.width = frame.displayWidth
      canvas.height = frame.displayHeight
    }
    ctx.drawImage(frame, 0, 0, canvas.width, canvas.height)
    frame.close()
    this.rendered++
    if (!this.ready) {
      this.ready = true
      this.o.onFirstFrame?.()
    }
  }

  private async run(): Promise<void> {
    if (this.disposed) return
    const ctrl = new AbortController()
    this.abort = ctrl
    const decoder = this.makeDecoder()
    const demuxer = new AnnexBDemuxer()
    let configured = false
    let sawKey = false
    let ts = 0

    let qs = ''
    if (this.quality) {
      const [size, bitRate] = this.quality.split(':')
      if (size && bitRate) qs = `?size=${size}&bit_rate=${bitRate}`
      this.quality = null // one-shot: a later reconnect must reuse the running stream
    }

    try {
      const res = await this.o.api.raw(`/v1/sessions/${this.o.sessionId}/video${qs}`, {
        signal: ctrl.signal,
      })
      if (res.status === 404 || res.status === 409) {
        const reason = lostThePhone(res.status, await res.json().catch(() => null))
        //: A 409 is a lost phone only when the service says so (`preempted_by_post`, `session_lost`);
        //: any other 409 is retried as before. Without this, a phone taken by automatic posting kept
        //: the stream retrying every few seconds against a session that was over.
        if (res.status === 409 && !reason) {
          this.retry(`http ${res.status}`)
          return
        }
        this.o.onStatus?.('сесія завершена')
        this.o.onSessionEnded?.(reason)
        return
      }
      if (!res.ok || !res.body) {
        this.retry(`http ${res.status}`)
        return
      }
      const reader = res.body.getReader()
      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        if (this.disposed) return
        this.watchdog.bytes(Date.now()) // any bytes (real frame OR keepalive) = alive
        this.bytesIn += value.length
        for (const au of demuxer.push(value)) {
          if (!configured && au.sps) {
            try {
              decoder.configure({ codec: codecFromSps(au.sps), optimizeForLatency: true })
              configured = true
            } catch (e) {
              console.warn('configure', e)
              this.retry('cfg')
              return
            }
          }
          if (!configured) continue
          if (!au.key && !sawKey) continue // wait for the first keyframe or we decode garbage
          if (au.key) sawKey = true
          try {
            decoder.decode(
              new EncodedVideoChunk({
                type: au.key ? 'key' : 'delta',
                timestamp: ts,
                data: joinAccessUnit(au),
              }),
            )
            ts += FRAME_TS_STEP
            this.fedAt.push(Date.now())
            if (this.fedAt.length > 30) this.fedAt.shift()
          } catch (e) {
            console.warn('decode', e)
          }
        }
      }
      this.retry('stream ended') // server closed it (hub drop / timeout) → reconnect
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        console.warn('video stream', e)
        this.retry('net err')
      }
    }
  }
}
